import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import authService from '../services/authService';

const OAuthCallbackPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isProcessing, setIsProcessing] = useState(true);

  useEffect(() => {
    const handleOAuthCallback = async () => {
      try {
        // Get parameters from URL
        const token = searchParams.get('token');
        const provider = searchParams.get('provider');
        const success = searchParams.get('success');
        const error = searchParams.get('error');
        const userParam = searchParams.get('user');

        if (error) {
          // Handle OAuth error
          toast.error('OAuth authentication failed. Please try again.');
          navigate('/login?error=oauth_failed');
          return;
        }

        if (!token || !provider || success !== 'true') {
          // Invalid callback parameters
          toast.error('Invalid OAuth callback. Please try again.');
          navigate('/login?error=oauth_invalid');
          return;
        }

        // Store the token and set it in authService using the standard key
        localStorage.setItem('authToken', token);
        authService.setToken(token);

        // Parse and store user data from URL parameter
        let user = null;
        if (userParam) {
          try {
            user = JSON.parse(decodeURIComponent(userParam));
            localStorage.setItem('user', JSON.stringify(user));
          } catch (error) {
          }
        }

        // If no user data from URL, try to get from authService
        if (!user) {
          user = authService.getCurrentUser();
        }
        
        // Show success message
        toast.success(`${provider.charAt(0).toUpperCase() + provider.slice(1)} login successful!`);

        // Debug logging

        // Redirect based on user type
        if (user?.user_type === 'agencies') {
          navigate('/research-landing');
        } else if (user?.user_type === 'brands') {
          navigate('/optimize');
        } else {
          // Fallback: if no user_type or unknown type, default to agencies (research-landing)
          navigate('/research-landing');
        }

      } catch (error) {
        toast.error('Failed to complete OAuth authentication. Please try again.');
        navigate('/login?error=oauth_failed');
      } finally {
        setIsProcessing(false);
      }
    };

    handleOAuthCallback();
  }, [navigate, searchParams]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full mx-4">
        <div className="text-center">
          {isProcessing ? (
            <>
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Completing Login</h2>
              <p className="text-gray-600">Please wait while we complete your authentication...</p>
            </>
          ) : (
            <>
              <div className="text-green-500 text-6xl mb-4">✓</div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Login Successful!</h2>
              <p className="text-gray-600">Redirecting you to the dashboard...</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default OAuthCallbackPage;
